import { Injectable, InjectionToken, Inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { NEVER, Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SKIP_INTERCEPTOR } from '../features/legal-entity-list/services/legal-entity.service';

// Narrow type rather than any to avoid DI + lint complaints
export type FetchSessionFn = () => Promise<{
  tokens?: {
    accessToken?: unknown; // may implement toString()
    idToken?: { payload?: { exp?: number } };
  };
}>;
import { LoggerService } from '../core/logging/logger.service';
import { SimpleDialog } from '../shared/models/simple-dialog';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';

// Injection token for providing a fetch session function (default: Amplify fetchAuthSession)
export const FETCH_SESSION_FN = new InjectionToken<FetchSessionFn>('FETCH_SESSION_FN', {
  providedIn: 'root',
  // Use factory so regular app runtime gets real Amplify function; tests can override
  factory: () => fetchAuthSession,
});

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    public router: Router,
    private logger: LoggerService,
    @Inject(FETCH_SESSION_FN) private fetchSession: FetchSessionFn,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage: "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };

    // Skip interceptor if context flag is set (e.g., for S3 uploads)
    if (req.context.get(SKIP_INTERCEPTOR)) {
      return next.handle(req);
    }

    // Skip token processing for non-API requests
    if (!originalUrl.includes(`${config.API_GATEWAY_ENDPOINT}`)) {
      return next.handle(req);
    }

    // Convert Promise to Observable and use switchMap to wait for completion
    return from(this.fetchSession()).pipe(
      switchMap((session) => {
        interface TokenLike {
          toString(): string;
        }

        const rawAccessToken = session?.tokens?.accessToken as TokenLike | undefined;
        const accessToken = rawAccessToken?.toString();
        const rawIdToken = session?.tokens?.idToken as TokenLike | undefined;
        const idToken = rawIdToken?.toString();
        const idTokenPayload = session.tokens?.idToken;
        const tokenExpirationTime = idTokenPayload?.payload?.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          this.logger.warn('Token expired, showing dialog');
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return NEVER;
        }

        // Add both tokens: access token for authorization, ID token for custom claims
        if (accessToken) {
          let headers = req.headers.set('Authorization', accessToken);

          // Add ID token as custom header if available for Lambda to access custom claims
          if (idToken) {
            headers = headers.set('X-ID-Token', idToken);
          }

          const modifiedReq = req.clone({ headers });
          return next.handle(modifiedReq);
        }

        // No token: treat as unauthenticated (not "expired") and redirect to login
        this.logger.warn(
          'No access token available; redirecting to /home (guard will trigger login)',
        );
        // Redirect to a protected route so AuthGuard handles SSO uniformly
        this.router.navigate(['/home']);
        return NEVER;
      }),
      catchError((error) => {
        this.logger.error('Error fetching auth session', error);

        // If it's a login request or other auth endpoint, allow without token
        if (originalUrl.includes('/auth/') || originalUrl.includes('/login')) {
          this.logger.info('Auth endpoint request, proceeding without token');
          return next.handle(req);
        }

        // For other endpoints, we might want to redirect to login
        // or allow the request to proceed and let the backend handle auth errors
        this.logger.warn('Proceeding with request without authentication');
        return next.handle(req);
      }),
    );
  }
}
